import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Typography, Button, Card, CardContent, CardActions, Chip, Dialog,
  DialogTitle, DialogContent, DialogActions, TextField, Select, MenuItem,
  FormControl, InputLabel, IconButton, Tooltip, Tabs, Tab, CircularProgress,
  Alert, Snackbar,
} from '@mui/material';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import SendIcon from '@mui/icons-material/Send';
import CloseIcon from '@mui/icons-material/Close';
import LinkIcon from '@mui/icons-material/Link';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import HistoryIcon from '@mui/icons-material/History';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingIcon from '@mui/icons-material/Pending';
import SyncIcon from '@mui/icons-material/Sync';
import DownloadIcon from '@mui/icons-material/Download';
import api from '../../services/api';

const ORANGE = '#F97316';

const TIPO_LABEL = { responsabilidade: 'Responsabilidade', anamnese: 'Anamnese', outro: 'Outro' };
const TIPO_COLOR = { responsabilidade: '#3b82f6', anamnese: '#22c55e', outro: '#a855f7' };

const VARIAVEIS = [
  { label: 'Nome do paciente', token: '{{PACIENTE_NOME}}' },
  { label: 'CPF do paciente', token: '{{PACIENTE_CPF}}' },
  { label: 'Nome do dentista', token: '{{DENTISTA_NOME}}' },
  { label: 'CRO do dentista', token: '{{DENTISTA_CRO}}' },
  { label: 'Data de hoje', token: '{{DATA}}' },
];

const QUILL_MODULES = {
  toolbar: [
    [{ header: [false, 2, 3] }],
    ['bold', 'italic', 'underline'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ align: [] }],
    ['clean'],
  ],
};

export default function Termos() {
  const [termos, setTermos] = useState([]);
  const [documentos, setDocumentos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(0);

  // Modal editor
  const [modalEditor, setModalEditor] = useState(false);
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState({ titulo: '', tipo: 'responsabilidade', conteudo: '' });
  const quillRef = useRef(null);

  // Modal enviar
  const [modalEnviar, setModalEnviar] = useState(false);
  const [termoSelecionado, setTermoSelecionado] = useState(null);
  const [pacientes, setPacientes] = useState([]);
  const [pacienteSelecionado, setPacienteSelecionado] = useState('');
  const [via, setVia] = useState('whatsapp');
  const [linkGerado, setLinkGerado] = useState('');
  const [enviando, setEnviando] = useState(false);

  const [snack, setSnack] = useState({ open: false, msg: '', severity: 'success' });

  const toast = (msg, severity = 'success') => setSnack({ open: true, msg, severity });

  const carregar = async () => {
    setLoading(true);
    try {
      const [t, d, p] = await Promise.all([
        api.get('/termos'),
        api.get('/termos/documentos'),
        api.get('/pacientes?limit=999'),
      ]);
      setTermos(Array.isArray(t) ? t : t?.data || []);
      const docs = Array.isArray(d) ? d : d?.data || [];
      setDocumentos(docs);
      const pList = Array.isArray(p) ? p : p?.data?.pacientes || p?.pacientes || [];
      setPacientes(pList);
      // Sincronizar automaticamente documentos pendentes com autentiqueId
      const pendentes = docs.filter(doc => doc.status === 'pendente' && doc.autentiqueId);
      if (pendentes.length > 0) {
        const atualizados = await Promise.allSettled(
          pendentes.map(doc => api.get(`/termos/documentos/${doc.id}/sincronizar`))
        );
        const houveAtualizacao = atualizados.some(
          (r, i) => r.status === 'fulfilled' && (r.value?.data || r.value)?.status === 'assinado'
        );
        if (houveAtualizacao) {
          const d2 = await api.get('/termos/documentos');
          setDocumentos(Array.isArray(d2) ? d2 : d2?.data || []);
        }
      }
    } catch (e) {
      toast('Erro ao carregar dados', 'error');
    }
    setLoading(false);
  };

  useEffect(() => { carregar(); }, []);

  const abrirEditor = (termo = null) => {
    if (termo) {
      setEditando(termo);
      setForm({ titulo: termo.titulo, tipo: termo.tipo, conteudo: termo.conteudo });
    } else {
      setEditando(null);
      setForm({ titulo: '', tipo: 'responsabilidade', conteudo: '' });
    }
    setModalEditor(true);
  };

  const inserirVariavel = (token) => {
    const editor = quillRef.current?.getEditor();
    if (!editor) return;
    const range = editor.getSelection(true);
    const index = range ? range.index : editor.getLength();
    editor.insertText(index, token, 'user');
    editor.setSelection(index + token.length, 0);
  };

  const salvarTermo = async () => {
    if (!form.titulo || !form.conteudo) return toast('Preencha título e conteúdo', 'error');
    try {
      if (editando) {
        await api.put(`/termos/${editando.id}`, form);
        toast('Termo atualizado!');
      } else {
        await api.post('/termos', form);
        toast('Termo criado!');
      }
      setModalEditor(false);
      carregar();
    } catch (e) {
      toast(e.response?.data?.error || 'Erro ao salvar', 'error');
    }
  };

  const clonar = async (termo) => {
    try {
      await api.post(`/termos/${termo.id}/clonar`);
      toast('Termo clonado para edição!');
      carregar();
    } catch (e) {
      toast('Erro ao clonar', 'error');
    }
  };

  const deletar = async (termo) => {
    if (!window.confirm(`Desativar "${termo.titulo}"?`)) return;
    try {
      await api.delete(`/termos/${termo.id}`);
      toast('Termo removido');
      carregar();
    } catch (e) {
      toast('Erro ao remover', 'error');
    }
  };

  const sincronizar = async (doc) => {
    try {
      const resp = await api.get(`/termos/documentos/${doc.id}/sincronizar`);
      const data = resp?.data || resp;
      if (data.status === 'assinado') {
        toast('Status atualizado: Assinado!', 'success');
        carregar();
      } else {
        toast('Documento ainda pendente de assinatura');
      }
      if (data.downloadUrl) window.open(data.downloadUrl, '_blank');
    } catch (e) {
      toast(e.response?.data?.error || 'Erro ao sincronizar', 'error');
    }
  };

  const abrirEnviar = (termo) => {
    setTermoSelecionado(termo);
    setPacienteSelecionado('');
    setLinkGerado('');
    setVia('whatsapp');
    setModalEnviar(true);
  };

  const enviarDocumento = async () => {
    if (!pacienteSelecionado) return toast('Selecione um paciente', 'error');
    setEnviando(true);
    try {
      const resp = await api.post('/termos/enviar', {
        pacienteId: pacienteSelecionado,
        termoId: termoSelecionado.id,
        via,
      });
      const data = resp?.data || resp;
      setLinkGerado(data.link);
      toast(via === 'whatsapp' ? 'Enviado via WhatsApp!' : 'Link gerado com sucesso!');
      carregar();
    } catch (e) {
      toast(e.response?.data?.error || 'Erro ao enviar', 'error');
    }
    setEnviando(false);
  };

  const copiarLink = (link) => {
    navigator.clipboard.writeText(link);
    toast('Link copiado!');
  };

  if (loading) return <Box display="flex" justifyContent="center" mt={8}><CircularProgress /></Box>;

  const termosPadrao = termos.filter(t => t.padrao);
  const termosClinica = termos.filter(t => !t.padrao);

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1100, mx: 'auto' }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Termos & Documentos</Typography>
          <Typography variant="body2" color="text.secondary">
            Gerencie termos de responsabilidade e anamneses para enviar aos pacientes
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => abrirEditor()}
          sx={{ bgcolor: ORANGE, '&:hover': { bgcolor: '#ea580c' }, borderRadius: 2 }}>
          Novo Termo
        </Button>
      </Box>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}>
        <Tab label={`Modelos Padrão (${termosPadrao.length})`} />
        <Tab label={`Meus Termos (${termosClinica.length})`} />
        <Tab label={`Enviados (${documentos.length})`} icon={<HistoryIcon sx={{ fontSize: 16 }} />} iconPosition="end" />
      </Tabs>

      {/* Termos Padrão */}
      {tab === 0 && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
          {termosPadrao.map(t => (
            <TermoCard key={t.id} termo={t} onClonar={clonar} onEnviar={abrirEnviar} readonly />
          ))}
        </Box>
      )}

      {/* Termos da clínica */}
      {tab === 1 && (
        <Box>
          {termosClinica.length === 0 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Você ainda não tem termos personalizados. Crie um novo ou clone um modelo padrão.
            </Alert>
          )}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
            {termosClinica.map(t => (
              <TermoCard key={t.id} termo={t} onEditar={abrirEditor} onDeletar={deletar} onEnviar={abrirEnviar} />
            ))}
          </Box>
        </Box>
      )}

      {/* Documentos enviados */}
      {tab === 2 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {documentos.length === 0 && <Alert severity="info">Nenhum documento enviado ainda.</Alert>}
          {documentos.map(d => (
            <Card key={d.id} variant="outlined" sx={{ borderRadius: 2 }}>
              <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
                  <Box>
                    <Typography fontWeight={600} fontSize="0.9rem">{d.paciente?.nome}</Typography>
                    <Typography variant="caption" color="text.secondary">{d.termo?.titulo}</Typography>
                  </Box>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Chip
                      icon={d.status === 'assinado' ? <CheckCircleIcon /> : <PendingIcon />}
                      label={d.status === 'assinado' ? `Assinado por ${d.nomeAssinante}` : 'Pendente'}
                      size="small"
                      color={d.status === 'assinado' ? 'success' : 'warning'}
                      variant="outlined"
                    />
                    {d.status === 'pendente' && (
                      <Tooltip title="Copiar link">
                        <IconButton size="small" onClick={() => copiarLink(`${window.location.origin}/assinar/${d.token}`)}>
                          <LinkIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {d.autentiqueId && d.status === 'assinado' && (
                      <Tooltip title="Baixar documento assinado">
                        <IconButton size="small" onClick={() => sincronizar(d)} sx={{ color: 'success.main' }}>
                          <DownloadIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {d.pdfPreenchidoUrl && d.status === 'assinado' && (
                      <Tooltip title="Baixar respostas preenchidas (PDF)">
                        <IconButton size="small" onClick={() => window.open(d.pdfPreenchidoUrl, '_blank', 'noopener,noreferrer')} sx={{ color: 'success.main' }}>
                          <DownloadIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </Box>
                {d.assinadoEm && (
                  <Typography variant="caption" color="success.main" display="block" mt={0.5}>
                    Assinado em {new Date(d.assinadoEm).toLocaleString('pt-BR')}
                  </Typography>
                )}
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      {/* Modal Editor */}
      <Dialog open={modalEditor} onClose={() => setModalEditor(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {editando ? 'Editar Termo' : 'Novo Termo'}
          <IconButton size="small" onClick={() => setModalEditor(false)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField label="Título" fullWidth value={form.titulo} onChange={e => setForm(f => ({ ...f, titulo: e.target.value }))} />
            <FormControl fullWidth>
              <InputLabel>Tipo</InputLabel>
              <Select value={form.tipo} label="Tipo" onChange={e => setForm(f => ({ ...f, tipo: e.target.value }))}>
                <MenuItem value="responsabilidade">Termo de Responsabilidade</MenuItem>
                <MenuItem value="anamnese">Anamnese</MenuItem>
                <MenuItem value="outro">Outro</MenuItem>
              </Select>
            </FormControl>
            <Box>
              <Typography variant="body2" fontWeight={600} mb={1}>Conteúdo do termo</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Clique para inserir automaticamente — o sistema preenche com os dados reais na hora de enviar:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1.5 }}>
                {VARIAVEIS.map(v => (
                  <Chip
                    key={v.token}
                    label={`+ ${v.label}`}
                    size="small"
                    clickable
                    onClick={() => inserirVariavel(v.token)}
                    sx={{
                      bgcolor: '#FFF7ED', color: ORANGE, fontWeight: 600, border: '1px solid #FED7AA',
                      '&:hover': { bgcolor: '#FFEDD5' },
                    }}
                  />
                ))}
              </Box>
              {form.tipo === 'anamnese' && (
                <Box sx={{ mb: 1.5 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                    Perguntas — o paciente responde antes de assinar. Depois de inserir, edite o texto "sua pergunta aqui":
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                    <Chip
                      label="+ Pergunta de texto"
                      size="small"
                      clickable
                      onClick={() => inserirVariavel('{{CAMPO: sua pergunta aqui}}')}
                      sx={{ bgcolor: '#EFF6FF', color: '#2563EB', fontWeight: 600, border: '1px solid #BFDBFE', '&:hover': { bgcolor: '#DBEAFE' } }}
                    />
                    <Chip
                      label="+ Pergunta Sim/Não"
                      size="small"
                      clickable
                      onClick={() => inserirVariavel('{{SIMNAO: sua pergunta aqui}}')}
                      sx={{ bgcolor: '#EFF6FF', color: '#2563EB', fontWeight: 600, border: '1px solid #BFDBFE', '&:hover': { bgcolor: '#DBEAFE' } }}
                    />
                  </Box>
                </Box>
              )}
              <Box sx={{
                '& .ql-toolbar': { borderTopLeftRadius: 8, borderTopRightRadius: 8, bgcolor: '#FAFAFA', borderColor: 'rgba(0,0,0,0.15)' },
                '& .ql-container': { borderBottomLeftRadius: 8, borderBottomRightRadius: 8, borderColor: 'rgba(0,0,0,0.15)', fontSize: 14 },
                '& .ql-editor': { minHeight: 260 },
              }}>
                <ReactQuill
                  ref={quillRef}
                  theme="snow"
                  value={form.conteudo}
                  onChange={html => setForm(f => ({ ...f, conteudo: html }))}
                  modules={QUILL_MODULES}
                  placeholder="Escreva o termo aqui, como num editor de texto normal..."
                />
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 1.5 }}>
          <Button onClick={() => setModalEditor(false)}>Cancelar</Button>
          <Button variant="contained" onClick={salvarTermo} sx={{ bgcolor: ORANGE, '&:hover': { bgcolor: '#ea580c' } }}>
            {editando ? 'Salvar' : 'Criar Termo'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal Enviar */}
      <Dialog open={modalEnviar} onClose={() => setModalEnviar(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography fontWeight={700}>Enviar para Assinar</Typography>
            <Typography variant="caption" color="text.secondary">{termoSelecionado?.titulo}</Typography>
          </Box>
          <IconButton size="small" onClick={() => setModalEnviar(false)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Paciente</InputLabel>
              <Select value={pacienteSelecionado} label="Paciente" onChange={e => setPacienteSelecionado(e.target.value)}>
                {pacientes.map(p => <MenuItem key={p.id} value={p.id}>{p.nome}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Enviar via</InputLabel>
              <Select value={via} label="Enviar via" onChange={e => setVia(e.target.value)}>
                <MenuItem value="whatsapp"><Box display="flex" alignItems="center" gap={1}><WhatsAppIcon sx={{ color: '#22c55e', fontSize: 18 }} />WhatsApp</Box></MenuItem>
                <MenuItem value="link"><Box display="flex" alignItems="center" gap={1}><LinkIcon sx={{ fontSize: 18 }} />Só gerar link</Box></MenuItem>
              </Select>
            </FormControl>
            {linkGerado && (
              <Alert severity="success" action={
                <Button size="small" startIcon={<ContentCopyIcon />} onClick={() => copiarLink(linkGerado)}>Copiar</Button>
              }>
                {linkGerado}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 1.5 }}>
          <Button onClick={() => setModalEnviar(false)}>Fechar</Button>
          {!linkGerado && (
            <Button variant="contained" startIcon={enviando ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
              onClick={enviarDocumento} disabled={enviando}
              sx={{ bgcolor: ORANGE, '&:hover': { bgcolor: '#ea580c' } }}>
              {enviando ? 'Enviando...' : 'Enviar'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={3500} onClose={() => setSnack(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snack.severity} onClose={() => setSnack(s => ({ ...s, open: false }))} variant="filled">
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}

function TermoCard({ termo, onEditar, onDeletar, onClonar, onEnviar, readonly }) {
  return (
    <Card variant="outlined" sx={{ borderRadius: 2, borderColor: readonly ? 'rgba(0,0,0,0.08)' : 'primary.light', position: 'relative' }}>
      {readonly && (
        <Chip label="Padrão" size="small"
          sx={{ position: 'absolute', top: 10, right: 10, fontSize: '0.65rem', bgcolor: '#f1f5f9', color: '#64748b' }} />
      )}
      <CardContent sx={{ pb: 1 }}>
        <Chip label={TIPO_LABEL[termo.tipo] || termo.tipo} size="small"
          sx={{ bgcolor: TIPO_COLOR[termo.tipo] + '20', color: TIPO_COLOR[termo.tipo], fontWeight: 600, fontSize: '0.7rem', mb: 1 }} />
        <Typography fontWeight={700} fontSize="0.95rem" mb={0.5}>{termo.titulo}</Typography>
        <Typography variant="caption" color="text.secondary" sx={{
          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden'
        }}>
          {termo.conteudo?.replace(/<[^>]+>/g, ' ').substring(0, 120)}...
        </Typography>
      </CardContent>
      <CardActions sx={{ px: 2, pb: 1.5, gap: 0.5 }}>
        <Button size="small" variant="contained" startIcon={<SendIcon />} onClick={() => onEnviar(termo)}
          sx={{ bgcolor: ORANGE, '&:hover': { bgcolor: '#ea580c' }, fontSize: '0.75rem' }}>
          Enviar
        </Button>
        {readonly ? (
          <Tooltip title="Clonar para editar">
            <IconButton size="small" onClick={() => onClonar(termo)}><ContentCopyIcon fontSize="small" /></IconButton>
          </Tooltip>
        ) : (
          <>
            <Tooltip title="Editar">
              <IconButton size="small" onClick={() => onEditar(termo)}><EditIcon fontSize="small" /></IconButton>
            </Tooltip>
            <Tooltip title="Remover">
              <IconButton size="small" color="error" onClick={() => onDeletar(termo)}><DeleteIcon fontSize="small" /></IconButton>
            </Tooltip>
          </>
        )}
      </CardActions>
    </Card>
  );
}
